import type {
  GetUserQuestionAnswersRankingResponse,
  InteractiveQuestionBankInteractiveQuestion,
  InteractiveQuestionBankResponse,
} from '@genially/contracts';
import type { AnyInteractiveQuestionItemData } from '@genially/document-model';
import { InteractiveQuestionType } from '@genially/document-model';
import type { HostPlayP2pClient } from '@genially/p2p-client';
import type { InteractiveQuestionState, PlayerUserAnswer } from '@genially/p2p-lib';
import { UserTrackingData } from '@genially/ui/build/hooks/useFetchUserTrackingData/models/UserTrackingData';
import { removeRemirrorHtmlTags } from '@genially/ui/build/utils/analytics/processQuestions';
import { compressFiles } from '@genially/ui/build/utils/compressFiles';
import { downloadFiles } from '@genially/ui/build/utils/downloadFiles';
import {
  FileType,
  generateBlobFromFile,
} from '@genially/ui/build/utils/generateBlobFromFile';
import { encodeCSVFields } from '@genially/utils/build/module';

import { dataFormater } from '../../../../../../../shared/application/utils/dataFormater';
import { PlayerAnswersInterpreter } from '../../../../../../../shared/infrastructure/PlayerAnswerInterpreter/PlayerAnswersInterpreter';
import { ViewApiService } from '../../../../../../../shared/infrastructure/ViewApiService';
import { nonScoreableInteractiveQuestionsList } from '../../../../../../../shared/types';
import { bankAnswerIndexFromId, bankAnswerTextFromId } from './bankAnswerText';

interface UserTrackingDataRanking extends UserTrackingData {
  position: number;
}

const AnswerValueMap = {
  true: 'v',
  false: 'x',
  undefined: '-',
};

const ANSWER_DELIMETER = ' / ';

const formatDateAsYYYYMMDD = (date: Date) => {
  const day = date.getDate();
  const month = date.getMonth() + 1;
  const year = date.getFullYear();

  return `${year}${month}${day}`;
};

const formatDateAsHHMM = (date: Date) => {
  const hours = date.getHours();
  const minutes = date.getMinutes();

  return `${hours}h${minutes}`;
};

const buildEmptyDataWithQuestions = (
  activities: AnyInteractiveQuestionItemData[],
): UserTrackingData[] => {
  return [
    UserTrackingData.of({
      activitiesDone: '',
      hits: '',
      userAlias: '',
      questions: activities.map(item => {
        // Random wrapper elements have no static `Question` of their own —
        // their question text is only known once the host draws a bank
        // question at runtime, which this static empty-state placeholder
        // (used when there is no tracked data at all) has no access to.
        const question = 'Question' in item ? item.Question : undefined;

        return {
          id: item.Id,
          type: item.Type,
          question,
          slideId: '',
          userAlias: '',
          answer: undefined,
          answersSelected: undefined,
          isCorrect: undefined,
        };
      }),
      firstAccessDate: new Date(),
      sessionDuration: 0,
      slidesVisited: 0,
      totalAccess: 0,
      visitedSlidesAmountOverTotalSlides: '',
      questionsAttempts: [],
    }),
  ];
};

const getRandomActivityBankIds = (
  activities: AnyInteractiveQuestionItemData[],
): Record<string, string> => {
  const bankIdByActivityId: Record<string, string> = {};

  activities.forEach(activity => {
    if (activity.Type === InteractiveQuestionType.Random && activity.BankId) {
      bankIdByActivityId[activity.Id] = activity.BankId;
    }
  });

  return bankIdByActivityId;
};

// The wire payload for a Random question's answer carries the drawn
// question's own concrete type (e.g. FillInTheBlanks) plus a flat
// `bankInteractiveQuestionId` — see buildRandomAnswerWirePayload.ts. Neither
// that field nor the per-type answer fields (`answerIds`/`userAnswer`/
// `answerText`) are part of any declared answer type, but they all survive on
// the host's `game.asJS.interactiveQuestionAnswers` untouched (mobx-keystone
// stores `answer` as a plain value, not a strict per-field schema), so the
// drawn question and the user's actual answer can both be read straight off
// each player's own answer — this is the per-user source of truth for what
// was answered (see `findBankQuestion` below). Which bank Question was drawn,
// though, is resolved separately via the host's broadcast draw (see
// `resolveBankQuestionByActivityId`), since that's shared by the whole
// session and also covers questions nobody answered. `isCorrect` sits next to
// `answer` (not inside it) on `PlayerUserAnswerModel`, for the same
// type-mismatch reason.
type RawRandomAnswer = {
  answerIds?: string[];
  userAnswer?: Record<string, string>;
  answerText?: string;
  isCorrect?: boolean;
};

const getRandomAnswerByUserAndActivity = (
  client: HostPlayP2pClient,
): {
  randomAnswerByUserAndActivity: Record<string, Record<string, RawRandomAnswer>>;
  answeredBankQuestionIdByActivityId: Record<string, string>;
} => {
  const randomAnswerByUserAndActivity: Record<
    string,
    Record<string, RawRandomAnswer>
  > = {};
  const answeredBankQuestionIdByActivityId: Record<string, string> = {};

  const interactiveQuestionAnswers = client.game.asJS
    .interactiveQuestionAnswers as Record<string, InteractiveQuestionState>;

  Object.values(interactiveQuestionAnswers).forEach(state => {
    state.playerUserAnswers.forEach((playerUserAnswer: PlayerUserAnswer) => {
      const { player, answer } = playerUserAnswer;
      const isCorrect = (playerUserAnswer as { isCorrect?: boolean }).isCorrect;
      const raw = answer as {
        interactiveQuestionId: string;
        bankInteractiveQuestionId?: string;
        answerIds?: string[];
        userAnswer?: Record<string, string>;
        answerText?: string;
      };

      if (!raw.bankInteractiveQuestionId) {
        return;
      }

      answeredBankQuestionIdByActivityId[raw.interactiveQuestionId] ??=
        raw.bankInteractiveQuestionId;

      randomAnswerByUserAndActivity[player.username] ??= {};
      randomAnswerByUserAndActivity[player.username][raw.interactiveQuestionId] = {
        answerIds: raw.answerIds,
        userAnswer: raw.userAnswer,
        answerText: raw.answerText,
        isCorrect,
      };
    });
  });

  return { randomAnswerByUserAndActivity, answeredBankQuestionIdByActivityId };
};

const resolveRandomAnswerText = (
  bankQuestion: InteractiveQuestionBankInteractiveQuestion,
  rawAnswer: RawRandomAnswer,
  t: (key: string) => string,
): string => {
  if (rawAnswer.answerText !== undefined) {
    return rawAnswer.answerText || '-';
  }

  if (rawAnswer.userAnswer) {
    const text = Object.entries(rawAnswer.userAnswer)
      .sort(
        ([blankIdA], [blankIdB]) =>
          bankAnswerIndexFromId(blankIdA) - bankAnswerIndexFromId(blankIdB),
      )
      .map(([, answerId]) => bankAnswerTextFromId(bankQuestion, answerId, t))
      .filter((answerText): answerText is string => !!answerText)
      .join(ANSWER_DELIMETER);

    return text || '-';
  }

  if (rawAnswer.answerIds) {
    const text = rawAnswer.answerIds
      .map(answerId => bankAnswerTextFromId(bankQuestion, answerId, t))
      .filter((answerText): answerText is string => !!answerText)
      .join(ANSWER_DELIMETER);

    return text || '-';
  }

  return '-';
};

// The host draws every Random element in the document up front, at genially
// load, regardless of whether anyone ever answers it, and that draw
// (`client.game.asJS.randomQuestionAssignments`, elementId -> bankQuestionId)
// is shared by the whole session — so the drawn bank Question can usually be
// resolved once per activity id rather than per user/answer. That broadcast
// can still be missing an entry though (e.g. it's reported over a postMessage
// connection that may not be up yet when the draw happens), so fall back to
// any player's own recorded `bankInteractiveQuestionId` for that activity —
// it won't cover a question nobody answered, but it's better than nothing.
const resolveBankQuestionByActivityId = (
  bankIdByActivityId: Record<string, string>,
  broadcastBankQuestionIdByActivityId: Record<string, string>,
  answeredBankQuestionIdByActivityId: Record<string, string>,
  banksByBankId: Record<string, InteractiveQuestionBankResponse>,
): Record<string, InteractiveQuestionBankInteractiveQuestion> => {
  const bankQuestionByActivityId: Record<
    string,
    InteractiveQuestionBankInteractiveQuestion
  > = {};

  Object.entries(bankIdByActivityId).forEach(([activityId, bankId]) => {
    const bankQuestionId =
      broadcastBankQuestionIdByActivityId[activityId] ??
      answeredBankQuestionIdByActivityId[activityId];
    const bankQuestion =
      bankQuestionId &&
      banksByBankId[bankId]?.interactiveQuestions.find(bq => bq.id === bankQuestionId);

    if (bankQuestion) {
      bankQuestionByActivityId[activityId] = bankQuestion;
    }
  });

  return bankQuestionByActivityId;
};

const findBankQuestion = (
  activityId: string,
  userAlias: string,
  bankQuestionByActivityId: Record<string, InteractiveQuestionBankInteractiveQuestion>,
  randomAnswerByUserAndActivity: Record<string, Record<string, RawRandomAnswer>>,
):
  | {
      bankQuestion: InteractiveQuestionBankInteractiveQuestion;
      rawAnswer: RawRandomAnswer | undefined;
    }
  | undefined => {
  const bankQuestion = bankQuestionByActivityId[activityId];

  if (!bankQuestion) {
    return undefined;
  }

  return {
    bankQuestion,
    rawAnswer: randomAnswerByUserAndActivity[userAlias]?.[activityId],
  };
};

const resolveRandomColumnLabel = (
  activityId: string,
  bankQuestionByActivityId: Record<string, InteractiveQuestionBankInteractiveQuestion>,
): string => {
  const bankQuestion = bankQuestionByActivityId[activityId];
  const label = bankQuestion && removeRemirrorHtmlTags(bankQuestion.question);

  return label ?? '';
};

const getIndividualTrackingData = ({
  data,
  geniallyName,
  t,
  interactiveQuestions,
  randomAnswerByUserAndActivity,
  bankQuestionByActivityId,
}: {
  geniallyName: string;
  data: UserTrackingData[];
  t: (key: string) => string;
  interactiveQuestions?: any[];
  randomAnswerByUserAndActivity: Record<string, Record<string, RawRandomAnswer>>;
  bankQuestionByActivityId: Record<string, InteractiveQuestionBankInteractiveQuestion>;
}) => {
  const isEmpty = Object.keys(data).length === 0;
  const userTrackingData = isEmpty
    ? buildEmptyDataWithQuestions(interactiveQuestions ?? [])
    : data;

  const commonHeader = `${window.location.href}\n${geniallyName}`;
  const newLine = encodeCSVFields(['', '', '', '', '']);

  const buildUserTrackingRows = () => {
    const headers = [t('__new.dashboard.analytics.table.columnUser')];

    const questions = userTrackingData[0].questions ?? [];
    const haveQuestions = questions.length > 0;

    if (haveQuestions) {
      headers.push(
        t('__new.dashboard.analytics.table.columnActivityDone'),
        t('__new.dashboard.analytics.table.columnSuccess'),
      );

      questions.forEach(q => {
        const label =
          q.type === InteractiveQuestionType.Random
            ? resolveRandomColumnLabel(q.id, bankQuestionByActivityId)
            : (q.question ?? '');

        headers.push(label);

        if (!nonScoreableInteractiveQuestionsList.includes(q.type)) {
          headers.push(t('__new.content.csv.answerSent'));
        }
      });
    }

    const header = encodeCSVFields(headers);

    const entries: string[] = [];

    if (!isEmpty) {
      for (const user of userTrackingData as UserTrackingData[]) {
        const values: string[] = [];

        values.push(user.userAlias);

        if (haveQuestions) {
          values.push(user.activitiesDone);
          values.push(user.hits);

          user.questions.forEach(question => {
            if (question.type === InteractiveQuestionType.Random) {
              const found = findBankQuestion(
                question.id,
                user.userAlias,
                bankQuestionByActivityId,
                randomAnswerByUserAndActivity,
              );

              values.push(
                AnswerValueMap[
                  `${found?.rawAnswer ? found.rawAnswer.isCorrect : question.isCorrect}`
                ],
              );
              values.push(
                found?.rawAnswer
                  ? resolveRandomAnswerText(found.bankQuestion, found.rawAnswer, t)
                  : (question.answer?.filter(Boolean).join(ANSWER_DELIMETER) ?? '-'),
              );

              return;
            }

            if (!nonScoreableInteractiveQuestionsList.includes(question.type)) {
              values.push(AnswerValueMap[`${question.isCorrect}`]);
            }

            const answerWithText = question.answer?.filter(Boolean);
            values.push(answerWithText ? answerWithText.join(ANSWER_DELIMETER) : '-');
          });
        }

        entries.push(encodeCSVFields(values));
      }
    }

    return `${commonHeader}\n${newLine}\n${newLine}\n${header}\n${entries.join('\n')}`;
  };

  return buildUserTrackingRows();
};

const getRankingData = ({
  data,
  geniallyName,
  t,
  rankingData,
  interactiveQuestions,
}: {
  geniallyName: string;
  data: UserTrackingData[];
  t: (key: string) => string;
  rankingData: GetUserQuestionAnswersRankingResponse | undefined;
  interactiveQuestions?: any[];
}) => {
  const isEmpty = data.length === 0;

  if (!rankingData) {
    return '';
  }

  const userTrackingData = data.length
    ? data
    : buildEmptyDataWithQuestions(interactiveQuestions ?? []);

  const resultData = rankingData.map((user, index) => {
    const userTracking = userTrackingData.find(
      userTrackingItem => userTrackingItem.userAlias === user.userAlias,
    );

    return {
      ...user,
      ...userTracking,
      position: index + 1,
    };
  });

  const commonHeader = `${window.location.href}\n${geniallyName}`;
  const newLine = encodeCSVFields(['', '', '', '', '']);

  const buildUserTrackingRows = (): string => {
    const headers = [
      t('__new.dashboard.analytics.table.columnPosition'),
      t('__new.dashboard.analytics.table.columnUser'),
    ];

    const questions = userTrackingData[0].questions ?? [];
    const haveQuestions = questions.length > 0;

    if (haveQuestions) {
      headers.push(
        t('__new.dashboard.analytics.table.columnActivityDone'),
        t('__new.dashboard.analytics.table.columnSuccess'),
      );
    }

    const header = encodeCSVFields(headers);

    const entries: string[] = [];

    if (!isEmpty) {
      for (const user of resultData as unknown as UserTrackingDataRanking[]) {
        const values: string[] = [];

        values.push(user.position.toString());
        values.push(user.userAlias);

        if (haveQuestions) {
          values.push(user.activitiesDone);
          values.push(user.hits);
        }

        entries.push(encodeCSVFields(values));
      }
    }

    return `${commonHeader}\n${newLine}\n${newLine}\n${header}\n${entries.join('\n')}`;
  };

  return buildUserTrackingRows();
};

export const downloadGameResultsFromClient = async (
  client: HostPlayP2pClient,
  t: (key: string) => string,
) => {
  const filesToDownload = [];
  const geniallyName = await ViewApiService.getGeniallyName(client.geniallyId);
  const geniallyData = await ViewApiService.getViewData(client.geniallyId);
  const geniallyContent = dataFormater(geniallyData);

  const playerAnswersInterpreter = PlayerAnswersInterpreter.from(
    client.geniallyId,
    client.game,
  );

  const userTrackingData = await playerAnswersInterpreter.interpretUserTrackingData();
  const rankingData = await playerAnswersInterpreter.interpretRanking();

  const bankIdByActivityId = getRandomActivityBankIds(geniallyContent.activities);
  const { randomAnswerByUserAndActivity, answeredBankQuestionIdByActivityId } =
    getRandomAnswerByUserAndActivity(client);
  const broadcastBankQuestionIdByActivityId = client.game.asJS.randomQuestionAssignments;
  const bankIds = Array.from(new Set(Object.values(bankIdByActivityId)));

  const banksByBankId: Record<string, InteractiveQuestionBankResponse> = {};

  await Promise.all(
    bankIds.map(async bankId => {
      const bank = await ViewApiService.getInteractiveQuestionBank(bankId);

      if (bank) {
        banksByBankId[bankId] = bank;
      }
    }),
  );

  const bankQuestionByActivityId = resolveBankQuestionByActivityId(
    bankIdByActivityId,
    broadcastBankQuestionIdByActivityId,
    answeredBankQuestionIdByActivityId,
    banksByBankId,
  );

  const csvFormat = getIndividualTrackingData({
    geniallyName,
    data: userTrackingData,
    t,
    interactiveQuestions: geniallyContent.activities,
    randomAnswerByUserAndActivity,
    bankQuestionByActivityId,
  });

  const userTrackingDataFile = {
    name: `${geniallyName}_individual.csv`,
    blob: generateBlobFromFile(csvFormat, FileType.CSV),
  };

  filesToDownload.push(userTrackingDataFile);

  const rankingCsvData = getRankingData({
    geniallyName,
    data: userTrackingData,
    t,
    rankingData,
    interactiveQuestions: geniallyContent.activities,
  });

  const rankingFile = {
    name: `${geniallyName}_ranking.csv`,
    blob: generateBlobFromFile(rankingCsvData, FileType.CSV),
  };

  filesToDownload.push(rankingFile);

  await downloadFiles(
    compressFiles(filesToDownload),
    `${geniallyName}_play_${formatDateAsYYYYMMDD(new Date())}_${formatDateAsHHMM(new Date())}.zip`,
  );
};
